import React from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, CollapseCard, TextField, Typography } from '@totalsoft/rocket-ui';
import { get, over, set } from '@totalsoft/change-tracking-react';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FlexBox from 'components/common/FlexBox';
import LabeledCheckbox from 'components/common/inputs/LabeledCheckbox';
import { valueOrDefault } from 'utils/functions';
import IconButton from 'components/common/buttons/IconButton';

const PartnerConsentComponent = ({ lens, templates, requiredDocTypes, partnerTypes, onRemove, validation, disabled }) => {
  const { t } = useTranslation();

  const isNew = typeof (lens.id |> get) === 'string';

  const template = { id: lens.templateId |> get, name: lens.templateName |> get };

  const requiredDocType = { id: lens.requiredDocTypeId |> get, name: lens.requiredDocTypeName |> get };

  const handleEntityChange = prefix => value => {
    over(lens, prev => ({ ...prev, [`${prefix}Id`]: value?.id || null, [`${prefix}Name`]: value?.name || null }));
  };

  const hasPartnerType = partnerTypeId => {
    const partnerTypeIds = lens.partnerTypeIds |> get;
    if (!partnerTypeIds || !Array.isArray(partnerTypeIds)) return false;
    return partnerTypeIds.includes(partnerTypeId);
  };

  const handlePartnerTypeChange = partnerTypeId => value => {
    const partnerTypeIds = lens.partnerTypeIds |> get;
    if (!value) (lens.partnerTypeIds |> set)(partnerTypeIds.filter(id => id !== partnerTypeId));
    else (lens.partnerTypeIds |> set)([...partnerTypeIds, partnerTypeId]);
  };

  return (
    <CollapseCard
      toggleOnHeaderClick
      title={
        <Typography variant='subtitle1' fontWeight='bold' color={isValid(validation) ? 'primary' : 'error'}>{`${
          (lens.code |> get) || ''
        } / ${(lens.name |> get) || ''}`}</Typography>
      }
      defaultExpanded={isNew}
      actions={isNew && <IconButton type='delete' color='error' onClick={() => onRemove(lens.id |> get)} disabled={disabled} />}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label={t('PartnerConsent.Code')}
            value={lens.code |> get |> valueOrDefault('')}
            onChange={lens.code |> set}
            error={!isValid(validation.code)}
            helperText={getErrors(validation.code)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label={t('PartnerConsent.Name')}
            value={lens.name |> get |> valueOrDefault('')}
            onChange={lens.name |> set}
            error={!isValid(validation.name)}
            helperText={getErrors(validation.name)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            isNumeric
            decimalScale={0}
            label={t('PartnerConsent.ValidityDays')}
            value={lens.validityDays |> get |> valueOrDefault('')}
            onChange={lens.validityDays |> set}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            label={t('PartnerConsent.Description')}
            value={lens.description |> get |> valueOrDefault('')}
            onChange={lens.description |> set}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            isClearable
            label={t('PartnerConsent.Template')}
            value={template}
            onChange={handleEntityChange('template')}
            options={templates}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            isClearable
            label={t('PartnerConsent.RequiredDocType')}
            value={requiredDocType}
            onChange={handleEntityChange('requiredDocType')}
            options={requiredDocTypes}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <LabeledCheckbox
            label={t('PartnerConsent.Active')}
            checked={lens.isActive |> get |> valueOrDefault(false)}
            onChange={lens.isActive |> set}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography fontWeight='bold' color='textPrimary' variant='subtitle2'>
            {t('PartnerConsent.PartnerTypes')}
          </Typography>
        </Grid>
        <FlexBox xs={{ direction: 'column', gap: 0 }} gap={2} ml={2} mt={1} wrap>
          {partnerTypes.map(partnerType => (
            <LabeledCheckbox
              key={partnerType.id}
              label={partnerType.name}
              checked={hasPartnerType(partnerType.id)}
              onChange={handlePartnerTypeChange(partnerType.id)}
              disabled={disabled}
            />
          ))}
        </FlexBox>
      </Grid>
    </CollapseCard>
  );
};

PartnerConsentComponent.propTypes = {
  lens: PropTypes.object,
  templates: PropTypes.array,
  requiredDocTypes: PropTypes.array,
  partnerTypes: PropTypes.array,
  onRemove: PropTypes.func,
  validation: PropTypes.object,
  disabled: PropTypes.bool
};

export default PartnerConsentComponent;
